import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { relativePastTimeAsText } from 'src/utilities/text.js';
import { t } from 'src/i18n';
import type { OrganizationDevice } from 'src/services/organization-devices-types';
import '../../library/components/d-popup.js';
import '../../library/editors/components/d-edit-access.js';
import '../../library/elements/d-wrap.js';
import { EditAccessDialog } from 'src/layout/parts/edit-access-dialog.js';
import type { UpdateAccessCommandAccessLevelEnum } from '@trinnvis/dabih-api-client';
import type { UserForAccess } from '../../library/editors/components/user-for-access';
import type { EditAccessResult } from './edit-access-result';
import type { EditAccessInput } from './edit-access-input';
import { accessProfiles } from '../../store/selectors';

interface UpdateUserAccessDetail {
  userUuid: string;
  accessLevel: UpdateAccessCommandAccessLevelEnum;
  accessExpires?: string;
  email: string;
  userType: 'EMPLOYEE' | 'CONTACT';
}

export class UpdateUserAccessEvent extends CustomEvent<UpdateUserAccessDetail> {
  constructor(detail: UpdateUserAccessDetail) {
    super('update-user-access', {
      composed: true,
      bubbles: true,
      detail: detail,
    });
  }
}
/**
 *
 * @fires update-user-access
 */
@customElement('d-organization-edit-access')
export class DOrganizationEditAccess extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }

    d-wrap {
      margin: -12px 0;
      padding: 12px 0;
      cursor: pointer;
    }

    d-wrap:hover {
      background: hsla(0, 0%, 100%, 0.2);
    }

    .devices {
      margin: 0 0 4px 0;
      font-size: 13px;
      color: hsl(0, 0%, 40%);
    }
  `;
  @property({ type: Array })
  users!: UserForAccess[];
  @property({ type: Object })
  currentUser!: UserForAccess;
  @property({ type: Object })
  editUser!: UserForAccess;

  @property({ type: String })
  ownerEmail!: string;

  @property({ type: Array })
  devices: OrganizationDevice[] = [];

  private get displayName() {
    let result = this.editUser.name;
    if (this.editUser.partnerName) {
      result += ' (' + this.editUser.partnerName + ')';
    }
    return result;
  }

  private get accessDisplay() {
    let result = '';
    const profiles = accessProfiles().filter((p) => {
      return p.name === this.editUser.accessLevel;
    });
    if (profiles.length) {
      result = profiles[0].displayName;
    }
    if (this.editUser.accessExpires) {
      result += ' til ' + LocalDate.fromString(this.editUser.accessExpires).toStringForDisplay();
    }
    return result;
  }

  render() {
    return html`
      <d-wrap split @click=${this.onEdit}>
        <div>${this.displayName}</div>
        <div>${this.accessDisplay}</div>
      </d-wrap>
      ${this.renderDevices()}
    `;
  }

  private renderDevices() {
    if (this.devices.length === 0) {
      return nothing;
    }
    return html`
      <div class="devices">
        ${this.devices.map(
          (device) => html`
            <div>
              ${device.deviceName} –
              ${device.lastSynced
                ? t('access.devices.lastSynced', { time: relativePastTimeAsText(device.lastSynced) })
                : t('access.devices.neverSynced')}
            </div>
          `,
        )}
      </div>
    `;
  }

  private async onEdit(e: Event) {
    e.stopPropagation();
    const input: EditAccessInput = {
      displayName: this.displayName,
      editUser: this.editUser,
      isOwner: this.editUser.email === this.ownerEmail,
      isSelf: this.editUser.uuid === this.currentUser.uuid,
      originalEmail: this.editUser.email,
      otherUserEmails: this.users
        .filter((u) => u.uuid !== this.editUser.uuid)
        .map((u) => u.email)
        .filter((email) => email !== ''),
    };
    const result: EditAccessResult = await EditAccessDialog.open(input);
    if (result.action === 'done') {
      this.dispatchEvent(
        new UpdateUserAccessEvent({
          userUuid: this.editUser.uuid,
          email: result.email,
          accessLevel: result.accessLevel,
          userType: this.isNotEmpty(this.editUser.partnerName) ? 'CONTACT' : 'EMPLOYEE',
          accessExpires: result.accessExpires,
        }),
      );
    }
  }

  private isNotEmpty(n: string | undefined) {
    return n !== '' && n !== undefined;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-organization-edit-access': DOrganizationEditAccess;
  }
}
